import {
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { FiHelpCircle } from "react-icons/fi";
import {
  getBoardPresentations,
  subscribeBoardPresentations,
} from "@/config/boardPresentation";
import { findMixLineupEntry } from "@/config/mixLineup";
import { BOARD_CARDS } from "@/features/settings/components/sections/boardCards";
import { useBoardR18ThreadCreationAllowed } from "@/features/settings/hooks/useBoardR18ThreadCreationAllowed";
import { useMixCandidateSlugs } from "@/features/settings/hooks/useMixCandidateSlugs";
import {
  createBoardSelectionMediator,
  isBoardEnabled,
} from "@/features/settings/mediators/boardSelectionMediator";
import {
  isSettingsEvent,
  type SettingsEvent,
} from "@/features/settings/mediators/settingsEvents";
import {
  MIX_MAX_BOARD_MESSAGE,
  MIX_MAX_ENABLED_BOARDS,
  type MixBoardSettings,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import { useIsDesktop } from "@/shared/hooks";
import { cn } from "@/shared/lib";
import {
  getAddedBoardNames,
  subscribeAddedBoardNames,
} from "@/shared/lib/addedBoardNames";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

/** 最後の1板を外そうとしたときに出す説明。 */
export const MIX_MIN_BOARD_MESSAGE = "1個以上の板を選択する必要があります";

interface BoardSelectionCardsProps {
  /** ラインナップ順の登録済み総合カタログ対象slug。 */
  registeredSlugs: readonly string[];
  className?: string;
  /** フィルタ内では説明をツールチップへ移し、高さ44pxに収める。 */
  compact?: boolean;
}

interface BoardSelectionCardsViewProps extends BoardSelectionCardsProps {
  readonly boardNames: Readonly<Record<string, string>>;
  readonly r18ThreadCreationAllowedBySlug: Readonly<Record<string, boolean>>;
  readonly boardSettings: MixBoardSettings;
  readonly isDesktop: boolean;
  readonly minBoardWarning: boolean;
  readonly maxBoardWarning?: boolean;
  readonly openHelp: string | null;
  readonly alignHelpRight: boolean;
  readonly onEvent: (event: SettingsEvent) => void;
}

function BoardR18BadgeView({
  isVisible,
}: {
  readonly isVisible: boolean;
}): React.ReactNode {
  return isVisible ? (
    <strong className="rounded bg-destructive px-1 text-[0.625rem] font-bold leading-4 text-destructive-foreground">
      R-18
    </strong>
  ) : null;
}

/**
 * 総合カタログに混ぜる板をカードで選ぶ基本設定。設定画面と初回パネルで共用する。
 * 選択が0個になる操作は握りつぶし、理由を明示する(空のカタログを作らせない)。
 */
export const BoardSelectionCards: React.FunctionComponent<
  BoardSelectionCardsProps
> = ({
  registeredSlugs: runtimeSlugs,
  className,
  compact = false,
}: BoardSelectionCardsProps) => {
  const registeredSlugs = useMixCandidateSlugs(runtimeSlugs);
  const r18ThreadCreationAllowedBySlug =
    useBoardR18ThreadCreationAllowed(registeredSlugs);
  const addedBoards = useMixStore((state) => state.addedBoards);
  const registeredNames = useSyncExternalStore(
    subscribeAddedBoardNames,
    getAddedBoardNames,
    getAddedBoardNames,
  );
  const boardSettings = useMixStore((state) => state.boardSettings);
  const setBoardEnabled = useMixStore((state) => state.setBoardEnabled);
  const isDesktop = useIsDesktop();
  const initialModel = useRef({ boardSettings, registeredSlugs });
  const mediator = useMemo(
    () =>
      createBoardSelectionMediator(
        initialModel.current.boardSettings,
        initialModel.current.registeredSlugs,
        (effect) => setBoardEnabled(effect.slug, effect.enabled),
      ),
    [setBoardEnabled],
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (
      !isSettingsEvent(event) ||
      (event.type !== "settings/board-selection-synced" &&
        event.type !== "settings/board-selection-toggled" &&
        event.type !== "settings/board-selection-all-toggled" &&
        event.type !== "settings/board-help-toggled" &&
        event.type !== "settings/board-help-dismissed")
    ) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const state = useMediatorSnapshot(mediator);

  useLayoutEffect(() => {
    mediator.dispatch({
      type: "settings/board-selection-synced",
      boardSettings,
      registeredSlugs,
    });
  }, [boardSettings, mediator, registeredSlugs]);

  return (
    <ComponentScope node={node}>
      <BoardSelectionCardsView
        boardNames={{
          ...Object.fromEntries(
            Object.entries(registeredNames).filter(
              ([slug]) =>
                !BOARD_CARDS[slug] ||
                addedBoards.some((board) => board.slug === slug),
            ),
          ),
          ...Object.fromEntries(
            addedBoards.map((board) => [board.slug, board.boardName]),
          ),
        }}
        registeredSlugs={registeredSlugs}
        r18ThreadCreationAllowedBySlug={r18ThreadCreationAllowedBySlug}
        className={className}
        compact={compact}
        boardSettings={boardSettings}
        isDesktop={isDesktop}
        minBoardWarning={state.minBoardWarning}
        maxBoardWarning={state.maxBoardWarning}
        openHelp={state.openHelp}
        alignHelpRight={state.alignHelpRight}
        onEvent={(event): void => {
          node.dispatch(event);
        }}
      />
    </ComponentScope>
  );
};

export const BoardSelectionCardsView: React.FunctionComponent<
  BoardSelectionCardsViewProps
> = ({
  registeredSlugs,
  boardNames,
  r18ThreadCreationAllowedBySlug,
  className,
  compact = false,
  boardSettings,
  isDesktop,
  minBoardWarning,
  maxBoardWarning,
  openHelp,
  alignHelpRight,
  onEvent,
}: BoardSelectionCardsViewProps) => {
  const descriptionBaseId = useId();
  const presentations = useSyncExternalStore(
    subscribeBoardPresentations,
    getBoardPresentations,
    getBoardPresentations,
  );

  useLayoutEffect(() => {
    if (openHelp === null) return;
    const dismissOutside = (event: PointerEvent): void => {
      if (
        event.target instanceof Element &&
        event.target.closest("[data-board-help]")
      )
        return;
      onEvent({ type: "settings/board-help-dismissed" });
    };
    const dismissEscape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      // 説明だけを閉じ、背後のフィルタモーダルは開いたままにする。
      event.preventDefault();
      event.stopPropagation();
      onEvent({ type: "settings/board-help-dismissed" });
    };
    document.addEventListener("pointerdown", dismissOutside);
    document.addEventListener("keydown", dismissEscape, true);
    return (): void => {
      document.removeEventListener("pointerdown", dismissOutside);
      document.removeEventListener("keydown", dismissEscape, true);
    };
  }, [onEvent, openHelp]);

  const enabledCount = registeredSlugs.filter((slug) =>
    isBoardEnabled(boardSettings, slug),
  ).length;

  const allSelected =
    registeredSlugs.length > 0 && enabledCount === registeredSlugs.length;

  function handleToggle(slug: string, enabled: boolean): void {
    onEvent({
      type: "settings/board-selection-toggled",
      slug,
      enabled,
    });
  }

  return (
    <fieldset className={cn("m-0 min-w-0 border-0 px-4 py-3", className)}>
      <legend className="sr-only">表示する板</legend>
      <ul
        className={cn(
          "m-0 list-none p-0",
          compact ? "flex flex-wrap gap-2" : "flex flex-col gap-3",
        )}
      >
        {compact && (
          <li>
            <button
              type="button"
              aria-pressed={allSelected}
              className={cn(
                "board-selection-all h-11 rounded-lg border border-foreground px-3 text-xs font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground",
                allSelected
                  ? "bg-foreground text-background"
                  : "bg-transparent text-foreground",
              )}
              onClick={(): void =>
                onEvent({ type: "settings/board-selection-all-toggled" })
              }
            >
              すべて
            </button>
          </li>
        )}
        {registeredSlugs.map((slug) => {
          const entry = findMixLineupEntry(slug);
          const checked = isBoardEnabled(boardSettings, slug);
          const card = BOARD_CARDS[slug];
          const imageUrl = presentations[slug]?.imageUrl;
          const descriptionId = `${descriptionBaseId}-${slug}`;
          const showHelp = compact && !isDesktop && Boolean(card?.description);
          return (
            <li key={slug} className="relative">
              <label
                title={compact && isDesktop ? card?.description : undefined}
                className={cn(
                  "relative flex cursor-pointer items-center rounded-lg border transition-colors focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary",
                  compact
                    ? "board-selection-compact h-11 w-fit gap-2 bg-transparent px-3"
                    : "board-selection-card w-full gap-4 bg-card p-4",
                  compact
                    ? checked
                      ? "border-[var(--board-selection-color)] ring-1 ring-[var(--board-selection-color)]"
                      : "border-border hover:border-secondary"
                    : checked
                      ? "border-primary ring-1 ring-primary"
                      : "border-border hover:border-secondary",
                )}
              >
                {imageUrl == null ? (
                  <span
                    aria-hidden="true"
                    className={cn(
                      "flex flex-none items-center justify-center rounded-md bg-muted",
                      compact ? "h-7 w-7 text-lg" : "h-14 w-14 text-3xl",
                    )}
                  >
                    {entry?.emoji ?? "🧩"}
                  </span>
                ) : (
                  <img
                    src={imageUrl}
                    alt=""
                    width={compact ? 28 : 56}
                    height={compact ? 28 : 56}
                    className={cn(
                      "flex-none rounded-md object-cover",
                      compact ? "h-7 w-7" : "h-14 w-14",
                    )}
                  />
                )}
                <span
                  className={cn(
                    "items-center gap-x-2",
                    compact
                      ? "flex whitespace-nowrap"
                      : "grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto]",
                  )}
                >
                  <b
                    className={cn(
                      "font-bold text-foreground",
                      compact ? "text-xs" : "text-base",
                    )}
                  >
                    {boardNames[slug] ?? card?.name ?? slug}
                  </b>
                  <BoardR18BadgeView
                    isVisible={r18ThreadCreationAllowedBySlug[slug] === true}
                  />
                  {!compact && (
                    <small className="col-span-2 mt-1 block text-xs text-muted-foreground">
                      {card?.description ?? ""}
                    </small>
                  )}
                </span>
                <input
                  type="checkbox"
                  className={cn(
                    compact
                      ? "h-4 w-4 flex-none accent-[var(--board-selection-color)]"
                      : "sr-only",
                    showHelp && "mr-8",
                  )}
                  aria-describedby={
                    compact && card?.description ? descriptionId : undefined
                  }
                  checked={checked}
                  onChange={(
                    event: React.ChangeEvent<HTMLInputElement>,
                  ): void => handleToggle(slug, event.currentTarget.checked)}
                />
              </label>
              {showHelp && (
                <button
                  type="button"
                  data-board-help
                  className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  aria-label={`${card?.name ?? boardNames[slug] ?? slug}の説明`}
                  aria-expanded={openHelp === slug}
                  aria-controls={descriptionId}
                  aria-describedby={descriptionId}
                  onClick={(
                    event: React.MouseEvent<HTMLButtonElement>,
                  ): void => {
                    onEvent({
                      type: "settings/board-help-toggled",
                      slug,
                      alignRight:
                        event.currentTarget.getBoundingClientRect().left >
                        window.innerWidth / 2,
                    });
                  }}
                  onBlur={(): void =>
                    onEvent({ type: "settings/board-help-dismissed" })
                  }
                >
                  <FiHelpCircle aria-hidden="true" />
                </button>
              )}
              {compact && card?.description && (
                <p
                  id={descriptionId}
                  role="tooltip"
                  data-board-help
                  hidden={openHelp !== slug || !showHelp}
                  className={cn(
                    "absolute top-full z-30 mt-1 w-64 max-w-[calc(100vw-2rem)] rounded-md border border-border bg-card p-3 text-xs text-foreground shadow-lg",
                    alignHelpRight ? "right-0" : "left-0",
                  )}
                >
                  {card.description}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {minBoardWarning && (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {MIX_MIN_BOARD_MESSAGE}
        </p>
      )}
      {enabledCount > MIX_MAX_ENABLED_BOARDS ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          選択済みの板が20個を超えています。総合カタログには先頭の20個を表示します。設定で20個以下にしてください。
        </p>
      ) : maxBoardWarning ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {MIX_MAX_BOARD_MESSAGE}
        </p>
      ) : null}
    </fieldset>
  );
};
